// src/components/WeightChart.js
// src/components/WeightChart.js
import React, { useEffect, useState } from "react";
import { auth, db } from "../firebase";
import { collection, query, orderBy, onSnapshot } from "firebase/firestore";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

export default function WeightChart() {
  const [weights, setWeights] = useState([]);
  const [unit, setUnit] = useState("kg");
  const [range, setRange] = useState("30d");

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const q = query(
      collection(db, "users", user.uid, "weights"),
      orderBy("createdAt", "asc")
    );

    // 🔄 Live listener (instead of getDocs)
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => {
        const d = doc.data();
        return {
          id: doc.id,
          weight: d.weight,
          unit: d.unit,
          createdAt: d.createdAt ? d.createdAt.toDate() : null,
        };
      });
      console.log("Updated weight entries:", data);
      setWeights(data);
    });

    return () => unsubscribe();
  }, []);

  // ===== Filter Data by Range =====
  const filteredData = weights.filter((w) => {
    if (!w.createdAt) return false;
    const now = new Date();
    let cutoff = new Date();
    if (range === "7d") cutoff.setDate(now.getDate() - 7);
    else if (range === "30d") cutoff.setDate(now.getDate() - 30);
    else if (range === "90d") cutoff.setDate(now.getDate() - 90);
    else if (range === "1y") cutoff.setFullYear(now.getFullYear() - 1);
    else cutoff = new Date(0);

    return w.createdAt >= cutoff;
  });

  // ===== Prepare Chart Data with Safe Check =====
  const chartData = filteredData
    .map((w) => {
      if (!w.createdAt) {
        console.warn(`Entry missing createdAt →`, w);
        return null;
      }

      const numericWeight = parseFloat(w.weight);
      if (isNaN(numericWeight)) {
        console.warn(`Skipped invalid weight entry (id: ${w.id}) →`, w.weight);
        return null;
      }

      const value =
        unit === "lb"
          ? (numericWeight / 0.453592).toFixed(1)
          : numericWeight.toFixed(1);

      return {
        date: w.createdAt.toLocaleDateString(),
        weight: parseFloat(value),
      };
    })
    .filter(Boolean);

  // ===== Latest + Delta =====
  const latest = chartData.length ? chartData[chartData.length - 1] : null;
  const prev = chartData.length > 1 ? chartData[chartData.length - 2] : null;
  const delta =
    latest && prev && latest.weight && prev.weight
      ? (latest.weight - prev.weight).toFixed(1)
      : null;

  // ===== CSV EXPORT FUNCTION =====
  const exportCSV = () => {
    if (!chartData.length) return;

    const headers = ["Date,Weight (" + unit + ")"];
    const rows = chartData
      .filter((d) => d.weight !== null)
      .map((d) => `${d.date},${d.weight}`);
    const csvContent = [headers, ...rows].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "weight_data.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="chart-container">
      <h3>Weight Trend</h3>
      <div>
        Range:
        <select value={range} onChange={(e) => setRange(e.target.value)}>
          <option value="7d">7 days</option>
          <option value="30d">30 days</option>
          <option value="90d">90 days</option>
          <option value="1y">1 year</option>
          <option value="all">All</option>
        </select>
        Unit:
        <select value={unit} onChange={(e) => setUnit(e.target.value)}>
          <option value="kg">kg</option>
          <option value="lb">lb</option>
        </select>

        <button onClick={exportCSV} style={{ marginLeft: "10px" }}>
          Export CSV
        </button>
      </div>

      {latest && latest.weight !== null && (
        <div style={{ marginTop: "10px", marginBottom: "10px" }}>
          <strong>Latest:</strong> {latest.weight} {unit} on {latest.date}
          {delta !== null && (
            <span style={{ marginLeft: "10px" }}>
              (Change: {delta > 0 ? "+" : ""}
              {delta} {unit})
            </span>
          )}
        </div>
      )}

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData}>
          <Line type="monotone" dataKey="weight" stroke="#007bff" />
          <CartesianGrid stroke="#ccc" />
          <XAxis dataKey="date" />
          <YAxis />
          <Tooltip />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
